import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { Group, Layer, Stage, Rect } from "react-konva";
import { getDimensions } from "../formatted/CalculateImageDimensions";
import { formattedColorMaterial } from "../formatted/FormattedText";
import {
  azureSASTokenToUrl,
  formatAzureBlob,
} from "../formatted/FormattedAzureSasToken";
import { handleSelectOne } from "../../containers/container/utilsTable";
import { ImageSrc } from "./ImageSrc";
import ContainerZoomButton from "../../containers/container/ContainerZoomButton";
import myPackmatIcon from "../../static/assets/CompactingIcon.jpg";
import { useTranslation } from "react-i18next";
import {
  getAzureBlob,
  updateOutputSurface,
  deleteSegmentation,
  getDeletedSegmentations,
  propagateCategoryChange,
} from "../../services/container";
import WastePolygon from "./WastePolygon";
import WastePolygonModal from "./WastePolygonModal";
import RestoreDeletedSegmentations from "./RestoreDeletedSegmentations";
import {
  getCameraId,
  getOutputSurfaceBlobNameFromImageUrl,
} from "../../shared/utils/utils";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { getSizeRange, SIZE_RANGES } from "../../shared/utils/sizeRangeUtils";

const FillingMetricIcon = () => (
  <svg
    width="16"
    height="19"
    viewBox="0 0 16 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className="shrink-0"
  >
    <path
      d="M8 18.4C5.78333 18.4 3.89583 17.6333 2.3375 16.1C0.779167 14.5667 0 12.7 0 10.5C0 9.41667 0.208333 8.40417 0.625 7.4625C1.04167 6.52083 1.61667 5.68333 2.35 4.95L6.95 0.425C7.1 0.291667 7.26667 0.1875 7.45 0.1125C7.63333 0.0375 7.81667 0 8 0C8.18333 0 8.36667 0.0375 8.55 0.1125C8.73333 0.1875 8.9 0.291667 9.05 0.425L13.65 4.95C14.3833 5.68333 14.9583 6.52083 15.375 7.4625C15.7917 8.40417 16 9.41667 16 10.5C16 12.7 15.2208 14.5667 13.6625 16.1C12.1042 17.6333 10.2167 18.4 8 18.4ZM2.05 11.4H13.9C14.1 10.2 13.9875 9.175 13.5625 8.325C13.1375 7.475 12.7 6.83333 12.25 6.4L8 2.2L3.75 6.4C3.3 6.83333 2.85833 7.475 2.425 8.325C1.99167 9.175 1.86667 10.2 2.05 11.4Z"
      fill="#222222"
    />
  </svg>
);

const WeightMetricIcon = () => (
  <svg
    width="16"
    height="18"
    viewBox="0 0 16 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className="shrink-0"
  >
    <path
      d="M1.99693 16H13.9969L12.5719 6H3.42193L1.99693 16ZM7.99693 4C8.28026 4 8.51776 3.90417 8.70943 3.7125C8.9011 3.52083 8.99693 3.28333 8.99693 3C8.99693 2.71667 8.9011 2.47917 8.70943 2.2875C8.51776 2.09583 8.28026 2 7.99693 2C7.7136 2 7.4761 2.09583 7.28443 2.2875C7.09276 2.47917 6.99693 2.71667 6.99693 3C6.99693 3.28333 7.09276 3.52083 7.28443 3.7125C7.4761 3.90417 7.7136 4 7.99693 4ZM10.8219 4H12.5719C13.0719 4 13.5053 4.16667 13.8719 4.5C14.2386 4.83333 14.4636 5.24167 14.5469 5.725L15.9719 15.725C16.0553 16.325 15.9011 16.8542 15.5094 17.3125C15.1178 17.7708 14.6136 18 13.9969 18H1.99693C1.38026 18 0.876096 17.7708 0.48443 17.3125C0.0927632 16.8542 -0.0614035 16.325 0.0219298 15.725L1.44693 5.725C1.53026 5.24167 1.75526 4.83333 2.12193 4.5C2.4886 4.16667 2.92193 4 3.42193 4H5.17193C5.12193 3.83333 5.08026 3.67083 5.04693 3.5125C5.0136 3.35417 4.99693 3.18333 4.99693 3C4.99693 2.16667 5.2886 1.45833 5.87193 0.875C6.45526 0.291667 7.1636 0 7.99693 0C8.83026 0 9.5386 0.291667 10.1219 0.875C10.7053 1.45833 10.9969 2.16667 10.9969 3C10.9969 3.18333 10.9803 3.35417 10.9469 3.5125C10.9136 3.67083 10.8719 3.83333 10.8219 4Z"
      fill="#222222"
    />
  </svg>
);

const SortingErrorsMetricIcon = () => (
  <svg
    width="22"
    height="19"
    viewBox="0 0 22 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className="shrink-0"
  >
    <path
      d="M0 19L11 0L22 19H0ZM3.45 17H18.55L11 4L3.45 17ZM11 16C11.2833 16 11.5208 15.9042 11.7125 15.7125C11.9042 15.5208 12 15.2833 12 15C12 14.7167 11.9042 14.4792 11.7125 14.2875C11.5208 14.0958 11.2833 14 11 14C10.7167 14 10.4792 14.0958 10.2875 14.2875C10.0958 14.4792 10 14.7167 10 15C10 15.2833 10.0958 15.5208 10.2875 15.7125C10.4792 15.9042 10.7167 16 11 16ZM10 13H12V8H10V13Z"
      fill="#222222"
    />
  </svg>
);

const ActivitySideMetricCard = ({ icon, title, value }) => (
  <article className="flex items-center gap-2.5 w-full min-w-0 border border-gray-300 rounded-xl bg-white px-3 py-2.5">
    <div className="shrink-0 flex items-center">{icon}</div>
    <div className="flex flex-col min-w-0">
      <span className="text-sm font-normal text-[#5E5E5E] truncate">
        {title}
      </span>
      <span className="text-base font-bold text-[#222222] leading-tight">
        {value}
      </span>
    </div>
  </article>
);

export const CropImage = ({
  container,
  containersSelected,
  setContainersSelected,
  data,
  displayLegend,
  imgDivWidth,
  imgDivHeight,
  isTooltip,
  isMap,
  isCharacterization,
  materialsDropdownToDisplay,
  showCheckbox,
  displayAnomalyLegend,
  anomalyList,
  isActivity,
  onClickZoomButton,
  history,
  displayToBlock,
  setContainerActive,
  containerSortingError,
  anomaliesObjects,
  displayStatIgnored = false,
  fetchAllTemporalData,
  onAfterSegmentationUpdate,
  disableMaterialsOnHover = false,
  showNewObjects,
  setShowNewObjects,
  selectedSizeRanges = new Set(SIZE_RANGES),
  skipSurfaceDataFetch = false,
  isQualicheck,
  hideUnknownObjects = false,
}) => {
  const { t } = useTranslation();
  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );
  const viewMode = Cookies.get("viewMode") || "client";
  const [imgWidth, setImgWidth] = useState();
  const [imgHeight, setImgHeight] = useState();
  const [materialToDisplay, setMaterialToDisplay] = useState([]);
  const [wasteObjectsList, setWasteObjectsList] = useState([]);
  // Display-only filter: wasteObjectsList stays complete because it is saved back to output-surface.
  const displayedWasteObjects = useMemo(
    () =>
      hideUnknownObjects
        ? wasteObjectsList.filter((object) => {
            const cls = object?.cls?.toLowerCase();
            return cls !== "unknown" && cls !== "waste";
          })
        : wasteObjectsList,
    [wasteObjectsList, hideUnknownObjects],
  );
  const [hoveredWasteObjectId, setHoveredWasteObjectId] = useState(null);
  const [selectedAnomalie, setSelectedAnomalie] = useState(null);
  const [isModalView, setIsModalView] = useState(false);
  const [outputSurfaceData, setOutputSurfaceData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [hasDeletedSegmentations, setHasDeletedSegmentations] = useState(false);
  const [hasLoadedWasteObjects, setHasLoadedWasteObjects] = useState(false);
  const awaitingValidationMinutesThreshold = Number(
    process.env.REACT_APP_AWAITING_VALIDATION_MINUTES ?? 120,
  );
  const isLandscape = data.w_box > data.h_box ? true : false;
  const showConstructionLinesForDebug = false;

  const zoomButtonRef = useRef(null);
  const zoomTextRef = useRef(null);
  const zoomRectRef = useRef(null);

  const currentData = history?.find((item) => item._time === data._time);
  const weightKG = currentData?.mass;
  const packmat = currentData?.packmat;

  const handleMouseOverContainerBox = () => {
    if (zoomButtonRef.current) {
      zoomButtonRef.current.opacity(1);
      zoomTextRef.current.opacity(1);
      zoomRectRef.current.opacity(0.2);
      document.body.style.cursor = "pointer";
    }
  };

  const handleMouseLeaveContainerBox = () => {
    if (zoomButtonRef.current) {
      zoomButtonRef.current.opacity(0);
      zoomTextRef.current.opacity(0);
      zoomRectRef.current.opacity(0);
      document.body.style.cursor = "default";
    }
  };

  const fetchWasteObjects = useCallback(() => {
    setHasLoadedWasteObjects(false);

    if (skipSurfaceDataFetch) {
      setHasLoadedWasteObjects(true);
      return;
    }
    if (
      showNewObjects ||
      viewMode === "admin" ||
      !anomaliesObjects ||
      anomaliesObjects.length === 0
    ) {
      if (data?.image_url) {
        const containerId = container._measurement ?? container._id;
        if (containerId == null) {
          setHasLoadedWasteObjects(true);
          return;
        }
        getAzureBlob({
          blobUrl: formatAzureBlob(
            data?.image_url
              .replace("pictures", "output-surface")
              .replace("-R.jpeg", "-" + containerId + "-surface.json")
              .replace("-L.jpeg", "-" + containerId + "-surface.json")
              .replace(".jpeg", "-" + containerId + "-surface.json"),
          ),
        })
          .then((wasteObjects) => {
            if (wasteObjects.status === 200) {
              setOutputSurfaceData(wasteObjects.data);
              if (wasteObjects?.data?.object_segmentations) {
                const newObjectSegmentations =
                  wasteObjects.data.object_segmentations.map((object) => {
                    if (object.cls === "waste") {
                      return {
                        ...object,
                        cls: "unknown",
                        originalCls: "unknown",
                      };
                    }
                    return object;
                  });

                setWasteObjectsList(newObjectSegmentations);
              }
            }
          })
          .catch((error) => {
            console.error("Error while fetching waste objects:", error);
          })
          .finally(() => {
            setHasLoadedWasteObjects(true);
          });
      } else {
        setHasLoadedWasteObjects(true);
      }
    } else {
      setWasteObjectsList(anomaliesObjects);
      setHasLoadedWasteObjects(true);
    }
  }, [
    showNewObjects,
    viewMode,
    container,
    data,
    anomaliesObjects,
    skipSurfaceDataFetch,
  ]);

  useEffect(() => {
    fetchWasteObjects();
  }, [fetchWasteObjects]);

  const checkDeletedSegmentations = useCallback(async () => {
    if (!data?.image_url) {
      setHasDeletedSegmentations(false);
      return;
    }

    try {
      const cameraId = getCameraId(data.image_url);
      const containerId = container?._measurement ?? container?._id;
      if (!cameraId || !containerId) {
        setHasDeletedSegmentations(false);
        return;
      }

      const outputSurfaceBlob = getOutputSurfaceBlobNameFromImageUrl(
        data.image_url,
        containerId,
      );

      const response = await getDeletedSegmentations({
        blobName: outputSurfaceBlob,
      });
      const deletedCount = response.data?.deletedSegmentations?.length || 0;
      setHasDeletedSegmentations(deletedCount > 0);
    } catch (error) {
      console.error("Error checking deleted segmentations:", error);
      setHasDeletedSegmentations(false);
    }
  }, [data?.image_url, container?._measurement, container?._id]);

  useEffect(() => {
    checkDeletedSegmentations();
  }, [checkDeletedSegmentations]);

  const isRecentlyReceivedWithoutSegmentations = useMemo(() => {
    const hasLoadedOutputSurface =
      outputSurfaceData != null && Object.keys(outputSurfaceData).length > 0;
    const outputSurfaceSegmentations = Array.isArray(
      outputSurfaceData?.object_segmentations,
    )
      ? outputSurfaceData.object_segmentations
      : null;

    if (
      !hasLoadedWasteObjects ||
      !hasLoadedOutputSurface ||
      outputSurfaceSegmentations == null ||
      !data?._time ||
      outputSurfaceSegmentations.length > 0
    ) {
      return false;
    }

    const imageDate = new Date(data._time);
    if (Number.isNaN(imageDate.getTime())) {
      return false;
    }

    const thresholdMinutes =
      Number.isFinite(awaitingValidationMinutesThreshold) &&
      awaitingValidationMinutesThreshold > 0
        ? awaitingValidationMinutesThreshold
        : 120;
    const elapsedMinutes = (Date.now() - imageDate.getTime()) / 1000 / 60;
    return elapsedMinutes >= 0 && elapsedMinutes < thresholdMinutes;
  }, [
    hasLoadedWasteObjects,
    outputSurfaceData,
    data?._time,
    awaitingValidationMinutesThreshold,
  ]);

  if (data?.image_url) {
    getDimensions(
      azureSASTokenToUrl(data?.image_url),
      setImgWidth,
      setImgHeight,
    );
  }

  // Limited crop margin around the container keypoints (tighter on activity detail).
  const cropMarginPx = isActivity ? 8 : 40;
  let local_w_box = data.w_box + cropMarginPx * 2;
  let local_h_box = data.h_box + cropMarginPx * 2;
  let local_x_box = data.x_box - cropMarginPx;
  let local_y_box = data.y_box - cropMarginPx;

  // if container keypoints are outside the image, adapt the dimensions to fit the image
  if (local_x_box < 0) {
    local_w_box -= Math.abs(local_x_box);
    local_x_box = 0;
  }
  if (local_y_box < 0) {
    local_h_box -= Math.abs(local_y_box);
    local_y_box = 0;
  }
  // if after that the width and height of the box is still larger than the image, crop as well
  local_w_box = Math.min(local_w_box, imgWidth - local_x_box);
  local_h_box = Math.min(local_h_box, imgHeight - local_y_box);

  if (!isTooltip) {
    data["volume"] = container.volume;
    if (container.contentCategory && container.contentCategory.density) {
      data["density"] = container.contentCategory.density;
    } else {
      data["density"] = container.density;
    }
  }

  const anomalyToSideCheck = () => {
    if (isCharacterization === true) {
      return false;
    }
    return (
      !isMap &&
      data.anomalies !== 0 &&
      data.typeAnomaly &&
      Object.keys(data.typeAnomaly).length <= 2
    );
  };

  const anomalyToBottomCheck = () => {
    if (isCharacterization === true) {
      return false;
    }
    return (
      (isLandscape && !isTooltip) ||
      (!isMap &&
        data.anomalies !== 0 &&
        data.typeAnomaly &&
        Object.keys(data.typeAnomaly).length > 2)
    );
  };

  const updateShowAnomaly = (type) => {
    let newShowItem = [...materialToDisplay];

    const findIndex = materialToDisplay.findIndex((item) => item === type);
    if (findIndex === -1) {
      newShowItem.push(type);
    } else {
      newShowItem.splice(findIndex, 1);
    }
    setMaterialToDisplay(newShowItem);
  };

  let scaleFactor = Math.min(
    imgDivWidth / local_w_box,
    imgDivHeight / local_h_box,
  );

  const isAuthorizedMaterial = (reviewCls) => {
    if (!reviewCls) return false;
    const authorizedMaterials =
      outputSurfaceData?.waste_category_authorized_materials || [];
    return authorizedMaterials.some((material) => reviewCls === material.value);
  };

  const isAuthorizedForClientHover = (anomalie) => {
    if (!anomalie) return false;
    const clsForCheck = anomalie.cls.toLowerCase();
    return isAuthorizedMaterial(clsForCheck);
  };

  const refreshAfterSegmentationUpdate = async ({
    shouldCheckDeletedSegmentations = false,
  } = {}) => {
    fetchWasteObjects();

    if (shouldCheckDeletedSegmentations) {
      checkDeletedSegmentations();
    }

    if (typeof onAfterSegmentationUpdate === "function") {
      Promise.resolve(onAfterSegmentationUpdate()).catch((error) => {
        console.error(
          "Error refreshing page after segmentation update:",
          error,
        );
      });
      return;
    }

    await fetchAllTemporalData();
  };

  const handleDeleteSuggestion = async (
    polygonId,
    comment = "",
    options = {},
  ) => {
    const { accepted: deletionAccepted = false, reviewRequestType = "delete" } =
      options;
    setIsLoading(true);
    try {
      const containerId = container._measurement ?? container._id;
      if (!containerId) {
        throw new Error("Missing container id for output surface blob");
      }
      const outputSurfaceBlob = getOutputSurfaceBlobNameFromImageUrl(
        data.image_url,
        containerId,
      );

      const now = new Date().toISOString();
      const updatedList = wasteObjectsList.map((item) =>
        item.id === polygonId
          ? {
              ...item,
              comment: comment || item.comment,
              suggestedForDeletion: true,
              deleteSuggestionComment: comment,
              deleteSuggestionTime: now,
              reviewTime: now,
              reviewRequestType,
              reviewStatus: deletionAccepted ? item.reviewStatus : "pending",
              ...(deletionAccepted && { deletionAccepted: true }),
            }
          : item,
      );

      setWasteObjectsList(updatedList);
      setOutputSurfaceData((prevData) => ({
        ...prevData,
        object_segmentations: updatedList,
      }));

      const params = {
        data: {
          ...outputSurfaceData,
          anomalies: updatedList.length,
          object_segmentations: updatedList,
        },
        blobName: outputSurfaceBlob,
        captureTime: data._time,
      };

      await updateOutputSurface(params);

      if (deletionAccepted) {
        await deleteSegmentation({
          blobName: outputSurfaceBlob,
          segmentationId: polygonId,
          captureTime: data._time,
        });
        const filteredList = updatedList.filter(
          (item) => item.id !== polygonId,
        );
        setWasteObjectsList(filteredList);
        setOutputSurfaceData((prevData) => ({
          ...prevData,
          object_segmentations: filteredList,
          anomalies: filteredList.length,
        }));
        await updateOutputSurface({
          data: {
            ...outputSurfaceData,
            anomalies: filteredList.length,
            object_segmentations: filteredList,
          },
          blobName: outputSurfaceBlob,
          captureTime: data._time,
        });
        await refreshAfterSegmentationUpdate({
          shouldCheckDeletedSegmentations: true,
        });
        toast.success(t("Segmentation deleted successfully"), {
          className: "custom-toast",
        });
      } else {
        await refreshAfterSegmentationUpdate();
        toast.success(t("Deletion suggestion submitted"), {
          className: "custom-toast",
        });
      }
    } catch (error) {
      console.error(
        deletionAccepted
          ? "Error deleting segmentation"
          : "Error suggesting deletion",
        error,
      );
      toast.error(
        t(
          deletionAccepted
            ? "Error deleting segmentation"
            : "Error suggesting deletion",
        ),
        {
          className: "custom-toast",
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSegmentation = async (polygonId) => {
    setIsLoading(true);
    try {
      const outputSurfaceBlob = getOutputSurfaceBlobNameFromImageUrl(
        data.image_url,
        container._measurement,
      );

      await deleteSegmentation({
        blobName: outputSurfaceBlob,
        segmentationId: polygonId,
        captureTime: data._time,
      });

      const updatedList = wasteObjectsList.filter(
        (item) => item.id !== polygonId,
      );
      setWasteObjectsList(updatedList);
      setOutputSurfaceData((prevData) => ({
        ...prevData,
        object_segmentations: updatedList,
        anomalies: updatedList.length,
      }));

      await refreshAfterSegmentationUpdate({
        shouldCheckDeletedSegmentations: true,
      });
      toast.success(t("Segmentation deleted successfully"), {
        className: "custom-toast",
      });
    } catch (error) {
      console.error("Error deleting segmentation:", error);
      toast.error(t("Error deleting segmentation"), {
        className: "custom-toast",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleObjectSegmentation = async (
    polygonId,
    comment,
    reviewCls,
    type,
    reviewRequestType = "category",
    options = {},
  ) => {
    setIsLoading(true);
    const containerId = container._measurement ?? container._id;
    const outputSurfaceBlob = getOutputSurfaceBlobNameFromImageUrl(
      data.image_url,
      containerId,
    );

    let updatedList = null;
    let sourceObject = options.sourceObject || null;
    if (type === "client_review") {
      updatedList = wasteObjectsList.map((item) =>
        item.id === polygonId
          ? {
              ...item,
              comment,
              reviewCls,
              reviewTime: new Date().toISOString(),
              reviewRequestType,
              reviewStatus: "pending",
              oldCls: item.cls,
              originalCls: item.cls,
            }
          : item,
      );
    } else if (type === "validation") {
      // Admin: apply category directly — no reviewCls (not a correction request).
      // Keep existing reviewTime so validation does not overwrite the client request date.
      updatedList = wasteObjectsList.map((item) =>
        item.id === polygonId
          ? {
              ...item,
              comment,
              reviewCls: null,
              reviewTime: item.reviewTime,
              reviewRequestType,
              cls: reviewCls,
              originalCls: reviewCls,
              color: formattedColorMaterial(reviewCls),
              reviewStatus: "accepted",
              oldCls: item.cls,
              isAuthorizedMaterial:
                outputSurfaceData?.waste_category_authorized_materials
                  ? isAuthorizedMaterial(reviewCls)
                  : item.isAuthorizedMaterial,
            }
          : item,
      );
    }

    sourceObject =
      sourceObject ||
      updatedList?.find((item) => item.id === polygonId) ||
      null;

    setWasteObjectsList(updatedList);
    setOutputSurfaceData((prevData) => ({
      ...prevData,
      object_segmentations: updatedList,
    }));
    const params = {
      data: {
        ...outputSurfaceData,
        anomalies: updatedList?.length ?? outputSurfaceData?.anomalies ?? 0,
        object_segmentations: updatedList,
      },
      blobName: outputSurfaceBlob,
      captureTime: data._time,
    };

    await updateOutputSurface(params);

    if (options.propagate && sourceObject && reviewRequestType === "category") {
      try {
        await propagateCategoryChange({
          containerId,
          captureTime: data._time,
          sourceObject,
          trackObjectId: sourceObject.trackObjectId,
          firstAppearanceAt: sourceObject.firstAppearanceAt,
          mode: type === "validation" ? "applied" : "pending",
          newCls: reviewCls,
          color: formattedColorMaterial(reviewCls),
          comment,
          reviewRequestType,
          isAuthorizedMaterial:
            outputSurfaceData?.waste_category_authorized_materials
              ? isAuthorizedMaterial(reviewCls)
              : undefined,
          skipSource: true,
        });
      } catch (propagateError) {
        console.error("Category propagation failed:", propagateError);
      }
    }

    await refreshAfterSegmentationUpdate();
    setIsLoading(false);
    if (type === "client_review") {
      toast.success(t("correctionRequestTaken"), {
        className: "custom-toast",
      });
    } else if (type === "validation") {
      toast.success(t("Modification validée"), {
        className: "custom-toast",
      });
    }
  };

  return (
    <section
      className={isActivity || containerSortingError ? "w-full" : undefined}
    >
      <section
        className={`visual-card-landscape ${isActivity || containerSortingError ? "w-full" : ""}`}
      >
        <div
          className={`flex flex-row w-full min-w-0 ${isActivity || containerSortingError ? "items-stretch" : ""}`}
        >
          {/* Image */}
          <div
            className={`visual-card-box-image visual-card-data-landscape ${
              isActivity || containerSortingError
                ? "flex-1 min-w-0 flex items-center justify-center"
                : ""
            }`}
            style={
              isActivity || containerSortingError
                ? { position: "relative" }
                : { margin: "auto 0", position: "relative" }
            }
            onClick={() => !isActivity}
          >
            {showCheckbox && (
              <div
                style={{ position: "absolute", top: 10, left: -5, zIndex: 10 }}
              >
                <input
                  type="checkbox"
                  className={`w-4 h-4 cursor-pointer`}
                  onChange={(event) =>
                    handleSelectOne(
                      event,
                      data,
                      setContainersSelected,
                      containersSelected,
                    )
                  }
                  checked={data.checked}
                />
              </div>
            )}

            {data?.image_url ? (
              <>
                <Stage width={imgDivWidth} height={imgDivHeight}>
                  <Layer>
                    <Group
                      clip={{
                        x: local_x_box,
                        y: local_y_box,
                        width: local_w_box,
                        height: local_h_box,
                      }}
                      x={
                        (imgDivWidth - local_w_box * scaleFactor) / 2 -
                        local_x_box * scaleFactor
                      }
                      y={
                        (imgDivHeight - local_h_box * scaleFactor) / 2 -
                        local_y_box * scaleFactor
                      }
                      scaleX={scaleFactor}
                      scaleY={scaleFactor}
                    >
                      {/* Get Image*/}
                      <ImageSrc src={azureSASTokenToUrl(data?.image_url)} />

                      {/* Render polygons first */}
                      {displayedWasteObjects
                        .filter((anomalie) => {
                          if (
                            !selectedSizeRanges ||
                            selectedSizeRanges.size === 0
                          )
                            return true;
                          const sizeRange = getSizeRange(
                            anomalie.longestSideMeters,
                          );
                          return selectedSizeRanges.has(sizeRange);
                        })
                        .map((anomalie) => {
                          if (
                            "cls" in anomalie === false ||
                            "points_konva" in anomalie === false
                          ) {
                            return null;
                          }

                          const type = anomalie.cls.toLowerCase();
                          const isUnknown =
                            anomalie.cls &&
                            anomalie.cls.toLowerCase() === "unknown" &&
                            !isCharacterization &&
                            !isQualicheck;
                          const hasComment = Boolean(anomalie.comment);
                          const hasReviewCls = Boolean(anomalie.reviewCls);
                          const hasDeleteDemand =
                            anomalie.suggestedForDeletion === true ||
                            Boolean(anomalie.deleteSuggestionComment);

                          const isVisible = showNewObjects
                            ? anomalie.isNewObject
                            : materialToDisplay.includes(type) ||
                              materialsDropdownToDisplay.includes(type) ||
                              (hasComment &&
                                (materialToDisplay.includes(
                                  "materials_with_comment",
                                ) ||
                                  materialsDropdownToDisplay.includes(
                                    "materials_with_comment",
                                  ))) ||
                              (hasReviewCls &&
                                (materialToDisplay.includes(
                                  "materials_with_review_cls",
                                ) ||
                                  materialsDropdownToDisplay.includes(
                                    "materials_with_review_cls",
                                  ))) ||
                              (hasDeleteDemand &&
                                (materialToDisplay.includes(
                                  "materials_with_demand_delete",
                                ) ||
                                  materialsDropdownToDisplay.includes(
                                    "materials_with_demand_delete",
                                  )));
                          const isHovered =
                            hoveredWasteObjectId === anomalie.id &&
                            !disableMaterialsOnHover &&
                            !isUnknown &&
                            (isCharacterization ||
                              viewMode === "admin" ||
                              !isAuthorizedForClientHover(anomalie));

                          return (
                            <WastePolygon
                              key={anomalie.id}
                              anomalie={anomalie}
                              isHovered={isHovered}
                              isVisible={isVisible}
                              setHoveredWasteObjectId={setHoveredWasteObjectId}
                              setSelectedAnomalie={(a) => {
                                setSelectedAnomalie(a);
                                setIsModalView(true);
                              }}
                              isCharacterization={isCharacterization}
                              isActivity={isActivity}
                              canvasWidth={local_w_box}
                              canvasHeight={local_h_box}
                              canvasOffsetX={local_x_box}
                              canvasOffsetY={local_y_box}
                              scaleFactor={scaleFactor}
                            />
                          );
                        })}

                      {/* Render labels on top */}
                      {displayedWasteObjects
                        .filter(
                          (anomalie) =>
                            "cls" in anomalie &&
                            "points_konva" in anomalie &&
                            (!selectedSizeRanges ||
                              selectedSizeRanges.size === 0 ||
                              selectedSizeRanges.has(
                                getSizeRange(anomalie.longestSideMeters),
                              )),
                        )
                        .map((anomalie) => {
                          const isUnknown =
                            anomalie.cls &&
                            anomalie.cls.toLowerCase() === "unknown" &&
                            !isCharacterization &&
                            !isQualicheck;

                          const isHovered =
                            hoveredWasteObjectId === anomalie.id &&
                            !disableMaterialsOnHover &&
                            !isUnknown &&
                            (isCharacterization ||
                              viewMode === "admin" ||
                              !isAuthorizedForClientHover(anomalie));

                          return (
                            <WastePolygon
                              key={`label-${anomalie.id}`}
                              anomalie={anomalie}
                              isHovered={isHovered}
                              isCharacterization={isCharacterization}
                              isActivity={isActivity}
                              canvasWidth={local_w_box}
                              canvasHeight={local_h_box}
                              canvasOffsetX={local_x_box}
                              canvasOffsetY={local_y_box}
                              scaleFactor={scaleFactor}
                              renderLabelOnly={true}
                            />
                          );
                        })}
                    </Group>

                    {/* AFFICHAGE DU CADRE DE REFERENCE CARRE*/}
                    {showConstructionLinesForDebug && (
                      <Rect
                        width={imgDivWidth}
                        height={imgDivHeight}
                        stroke="black"
                        strokeWidth={4}
                      />
                    )}

                    {onClickZoomButton && (
                      <Group
                        onMouseOver={() => {
                          if (displayToBlock) {
                            handleMouseOverContainerBox();
                          }
                        }}
                        onMouseLeave={() => handleMouseLeaveContainerBox()}
                        onClick={() => {
                          if (!isActivity) {
                            onClickZoomButton();
                            setContainerActive(container);
                          }
                        }}
                      >
                        <Rect
                          ref={zoomRectRef}
                          width={imgDivWidth}
                          height={imgDivHeight}
                          opacity={0}
                          fill="white"
                        />

                        <ContainerZoomButton
                          buttonRef={zoomButtonRef}
                          textRef={zoomTextRef}
                          imgDivWidth={imgDivWidth}
                          imgDivHeight={imgDivHeight}
                        />
                      </Group>
                    )}
                  </Layer>
                </Stage>
                {isRecentlyReceivedWithoutSegmentations &&
                  container.characterization && (
                    <div className="mt-2 text-center text-sm text-[#5e5e5e]">
                      {t("photoReceivedAwaitingValidation")}
                    </div>
                  )}
              </>
            ) : (
              <div className="no-data">
                {t("No waste detected or no image found")}{" "}
              </div>
            )}
          </div>
          {(isActivity || containerSortingError) && (
            <div
              className={`w-64 shrink-0 truncate pr-5 ${(history?.length ?? 0) <= 1 ? "self-center" : ""}`}
            >
              <div className="flex flex-col w-full gap-5">
                {!containerSortingError &&
                  container.activatedFillingLevelOption && (
                    <>
                      <ActivitySideMetricCard
                        icon={<FillingMetricIcon />}
                        title={t("Filling")}
                        value={`${data.filling_level ? data.filling_level : 0}%`}
                      />

                      {data.volume !== 0 && data.density ? (
                        <ActivitySideMetricCard
                          icon={<WeightMetricIcon />}
                          title={t("Weight")}
                          value={`${Math.round(weightKG)}kg`}
                        />
                      ) : null}

                      {packmat === true && (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: 4,
                          }}
                        >
                          <img
                            src={myPackmatIcon}
                            alt="Compaction icon"
                            style={{
                              height: 28,
                              verticalAlign: "middle",
                              marginRight: 5,
                            }}
                          />
                          <span className="text" style={{ marginLeft: 0 }}>
                            {t("Packmat")}
                          </span>
                        </span>
                      )}
                    </>
                  )}

                {container.activatedSortingErrorOption && (
                  <ActivitySideMetricCard
                    icon={<SortingErrorsMetricIcon />}
                    title={t("Anomalies")}
                    value={data.anomalies !== 0 ? data.anomalies : 0}
                  />
                )}
                {container.activatedSortingErrorOption &&
                  data.anomalies !== 0 && (
                    <div className="border border-gray-300 rounded-xl bg-white px-3 py-2.5">
                      <p className="m-0 mb-2 text-xl font-bold text-[#222222]">
                        {t("activityMaterialsTab")}
                      </p>
                      <div className="flex flex-col gap-1.5">
                        {anomalyList
                          .slice()
                          .sort((a, b) => {
                            const special = [
                              "materials_with_comment",
                              "materials_with_review_cls",
                              "materials_with_demand_delete",
                            ];
                            const aIsSpecial = special.includes(a.value);
                            const bIsSpecial = special.includes(b.value);

                            if (aIsSpecial && !bIsSpecial) return 1;
                            if (!aIsSpecial && bIsSpecial) return -1;

                            return a.value.localeCompare(b.value);
                          })
                          .map((anomaly, index) => {
                            const isChecked = materialToDisplay.includes(
                              anomaly.value,
                            );
                            return (
                              <button
                                key={anomaly._id || anomaly.value || index}
                                type="button"
                                className="flex flex-row items-center gap-2 min-w-0 w-full text-left bg-transparent border-0 p-0 cursor-pointer"
                                onClick={() => updateShowAnomaly(anomaly.value)}
                              >
                                <span
                                  className="w-2.5 h-2.5 rounded-full shrink-0"
                                  style={{ backgroundColor: anomaly.color }}
                                  aria-hidden
                                />
                                <span
                                  className="w-4 h-4 flex items-center justify-center border-2 border-gray-400 rounded shrink-0"
                                  style={{
                                    backgroundColor: isChecked
                                      ? "#9CA3AF"
                                      : "#FFFFFF",
                                  }}
                                  aria-hidden
                                >
                                  {isChecked ? (
                                    <i className="fas fa-check text-[10px] text-white"></i>
                                  ) : null}
                                </span>
                                <span className="text-sm font-normal text-[#5E5E5E] truncate">
                                  {anomaly.name}
                                </span>
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}
              </div>
              {displayStatIgnored && data.anomalies !== 0 && (
                <span className="inline-flex items-start text-[#5E5E5E] text-md mt-2 whitespace-normal">
                  <SortingErrorsMetricIcon />
                  <span className="ml-2">
                    {t(
                      "Anomalies marked as corrected, excluded from statistics",
                    )}
                  </span>
                </span>
              )}
              {isAkanthasAccount && viewMode === "admin" && (
                <div className="flex flex-col w-full gap-2 mt-2">
                  <button
                    className="w-5/6 bg-[#00A7A6] hover:bg-[#009493] text-white font-medium rounded-lg px-2 py-1 
                              text-center leading-snug whitespace-normal break-words shadow-sm transition-colors duration-200"
                    onClick={() => setShowNewObjects(!showNewObjects)}
                  >
                    {showNewObjects
                      ? t("Hide new objects")
                      : t("Show new objects")}
                  </button>
                </div>
              )}
              {hasDeletedSegmentations && (
                <div className="flex flex-col w-full gap-2 mt-2">
                  <button
                    className="w-5/6 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-lg px-2 py-1 
                              text-center leading-tight whitespace-normal break-words shadow-sm transition-colors duration-200 flex items-center justify-center gap-1.5"
                    onClick={() => setShowRestoreModal(true)}
                  >
                    <i className="fas fa-history text-xs"></i>
                    {t("Restore deleted segmentations")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Legend */}
        {displayLegend && (
          <div
            className={`flex ${isLandscape ? "w-full flex-row" : "w-full flex-col"}`}
          >
            {/* Anomalies list legend if lenght <= 2*/}
            {anomalyToSideCheck() && displayAnomalyLegend && (
              <section
                className={`flex visual-data visual-data-anomaly-tooltip ${isTooltip ? "visual-data-tooltip " : ""} `}
              >
                {Object.keys(data.typeAnomaly).map((type, index) => {
                  return (
                    <div key={type._id} className="visual-data-anomaly-type">
                      <div
                        className={`circle-content`}
                        style={{
                          backgroundColor: formattedColorMaterial(type),
                        }}
                      ></div>
                      <div
                        className={` ${isTooltip ? "" : "visual-data-anomaly-text"}`}
                      >
                        <p>
                          {t(type)} ({data.typeAnomaly[type]})
                        </p>
                        <button
                          className="button-nothing"
                          onClick={() => {
                            updateShowAnomaly(type);
                          }}
                        >
                          {materialToDisplay.includes(type)
                            ? t("Hide")
                            : t("Show")}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </section>
            )}
          </div>
        )}

        {selectedAnomalie && !disableMaterialsOnHover && (
          <WastePolygonModal
            isModalView={isModalView}
            setIsModalView={setIsModalView}
            selectedAnomalie={selectedAnomalie}
            imageUrl={data?.image_url}
            captureTime={data?._time}
            containerId={container?._measurement ?? container?._id}
            imgDivWidth={imgDivWidth}
            imgDivHeight={imgDivHeight}
            imgWidth={imgWidth}
            imgHeight={imgHeight}
            handleObjectSegmentation={handleObjectSegmentation}
            handleDeleteSegmentation={handleDeleteSegmentation}
            handleDeleteSuggestion={handleDeleteSuggestion}
            isLoading={isLoading}
          />
        )}

        <RestoreDeletedSegmentations
          isOpen={showRestoreModal}
          toggle={() => setShowRestoreModal(!showRestoreModal)}
          blobName={
            data?.image_url
              ? getOutputSurfaceBlobNameFromImageUrl(
                  data.image_url,
                  container?._measurement ?? container?._id,
                )
              : null
          }
          captureTime={data?._time}
          onRestore={() => {
            fetchWasteObjects();
            fetchAllTemporalData();
            checkDeletedSegmentations();
            if (typeof onAfterSegmentationUpdate === "function") {
              onAfterSegmentationUpdate();
            }
          }}
        />
      </section>

      {/* Anomaly legend for landscape or anomaly length > 2*/}
      {!anomalyToSideCheck() &&
        anomalyToBottomCheck() &&
        displayAnomalyLegend && (
          <article className="visual-type-anomaly m-2">
            {Object.keys(data.typeAnomaly).map((type, index) => {
              return (
                <div key={type._id} className="visual-data-anomaly-type">
                  <div
                    className={`circle-content`}
                    style={{ backgroundColor: formattedColorMaterial(type) }}
                  ></div>
                  <div
                    className={` ${isTooltip ? "" : "visual-data-anomaly-text"}`}
                  >
                    <p>
                      {t(type)} ({data.typeAnomaly[type]})
                    </p>
                    <button
                      className="button-nothing"
                      onClick={() => {
                        updateShowAnomaly(type);
                      }}
                    >
                      {materialToDisplay.includes(type) ? "Cacher" : "Afficher"}
                    </button>
                  </div>
                </div>
              );
            })}
          </article>
        )}
    </section>
  );
};
